import { supabase } from './supabase'
import type { Case, Document, Node, TimelineEvent, AnalysisReport, Payment, Judgment, ReminderConfig, Notification } from '../types'

// ============================================
// 案件 CRUD
// ============================================

export async function getCases(filters?: {
  status?: string
  caseType?: string
  partyRole?: string
  year?: number
}) {
  let query = supabase
    .from('cases')
    .select('*')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (filters?.status) query = query.eq('status', filters.status)
  if (filters?.caseType) query = query.eq('case_type', filters.caseType)
  if (filters?.partyRole) query = query.eq('party_role', filters.partyRole)
  if (filters?.year) {
    query = query.eq(
      'filing_date',
      supabase
        .from('cases')
        .select('filing_date')
        .gte('filing_date', `${filters.year}-01-01`)
        .lte('filing_date', `${filters.year}-12-31`)
    )
  }

  const { data, error } = await query
  if (error) throw error
  return data as Case[]
}

export async function getCaseById(id: string) {
  const { data, error } = await supabase
    .from('cases')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (error) throw error
  return data as Case
}

export async function createCase(caseData: Omit<Case, 'id' | 'user_id' | 'created_at' | 'updated_at'>) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('cases')
    .insert({ ...caseData, user_id: user.id })
    .select()
    .single()

  if (error) throw error
  return data as Case
}

export async function updateCase(id: string, updates: Partial<Case>) {
  const { data, error } = await supabase
    .from('cases')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as Case
}

export async function softDeleteCase(id: string) {
  const { error } = await supabase
    .from('cases')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

export async function batchDeleteCases(ids: string[]) {
  const { error } = await supabase
    .from('cases')
    .update({ deleted_at: new Date().toISOString() })
    .in('id', ids)

  if (error) throw error
}

// ============================================
// 文书管理
// ============================================

export async function getDocumentsByCaseId(caseId: string) {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('case_id', caseId)
    .is('deleted_at', null)
    .order('received_date', { ascending: false })

  if (error) throw error
  return data as Document[]
}

export async function createDocument(docData: Omit<Document, 'id' | 'created_at'>) {
  const { data, error } = await supabase
    .from('documents')
    .insert(docData)
    .select()
    .single()

  if (error) throw error
  return data as Document
}

export async function uploadFile(file: File, caseId: string) {
  const fileExt = file.name.split('.').pop() || 'pdf'
  const fileName = `${caseId}/${Date.now()}.${fileExt}`

  const { data, error } = await supabase.storage
    .from('documents')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    })

  if (error) throw error

  const { data: { publicUrl } } = supabase.storage
    .from('documents')
    .getPublicUrl(fileName)

  return publicUrl
}

// ============================================
// 程序节点
// ============================================

export async function getNodesByCaseId(caseId: string) {
  const { data, error } = await supabase
    .from('nodes')
    .select('*')
    .eq('case_id', caseId)
    .is('deleted_at', null)
    .order('end_date', { ascending: true })

  if (error) throw error
  return data as Node[]
}

export async function createNode(nodeData: Omit<Node, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('nodes')
    .insert(nodeData)
    .select()
    .single()

  if (error) throw error
  return data as Node
}

export async function bulkCreateNodes(nodes: Omit<Node, 'id' | 'created_at' | 'updated_at'>[]) {
  const { data, error } = await supabase
    .from('nodes')
    .insert(nodes)
    .select()

  if (error) throw error
  return data as Node[]
}

export async function updateNodeStatus(nodeId: string, status: Node['status']) {
  const updateData: Partial<Node> = { status }
  if (status === 'completed') {
    updateData.completed_at = new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('nodes')
    .update(updateData)
    .eq('id', nodeId)
    .select()
    .single()

  if (error) throw error
  return data as Node
}

// ============================================
// 时间线事件
// ============================================

export async function getTimelineEventsByCaseId(caseId: string) {
  const { data, error } = await supabase
    .from('timeline_events')
    .select('*')
    .eq('case_id', caseId)
    .is('deleted_at', null)
    .order('event_date', { ascending: false })

  if (error) throw error
  return data as TimelineEvent[]
}

export async function createTimelineEvent(eventData: Omit<TimelineEvent, 'id' | 'created_at'>) {
  const { data, error } = await supabase
    .from('timeline_events')
    .insert(eventData)
    .select()
    .single()

  if (error) throw error
  return data as TimelineEvent
}

// ============================================
// 分析报告
// ============================================

export async function getAnalysisByCaseAndPerspective(caseId: string, perspective: string) {
  const { data, error } = await supabase
    .from('analysis_reports')
    .select('*')
    .eq('case_id', caseId)
    .eq('perspective', perspective)
    .order('updated_at', { ascending: false })
    .limit(1)
    .single()

  if (error && error.code !== 'PGRST116') throw error // PGRST116 = no rows found
  return data as AnalysisReport | null
}

export async function upsertAnalysis(report: Omit<AnalysisReport, 'id' | 'created_at'>) {
  const existing = await getAnalysisByCaseAndPerspective(report.case_id, report.perspective)

  if (existing) {
    const { data, error } = await supabase
      .from('analysis_reports')
      .update({ content: report.content, checklist_status: report.checklist_status })
      .eq('id', existing.id)
      .select()
      .single()

    if (error) throw error
    return data as AnalysisReport
  } else {
    const { data, error } = await supabase
      .from('analysis_reports')
      .insert(report)
      .select()
      .single()

    if (error) throw error
    return data as AnalysisReport
  }
}

// ============================================
// 收款相关
// ============================================

export async function getPaymentsByCaseId(caseId: string) {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('case_id', caseId)
    .order('payment_date', { ascending: false })

  if (error) throw error
  return data as Payment[]
}

export async function createPayment(paymentData: Omit<Payment, 'id' | 'created_at'>) {
  const { data, error } = await supabase
    .from('payments')
    .insert(paymentData)
    .select()
    .single()

  if (error) throw error
  return data as Payment
}

export async function getJudgmentsByCaseId(caseId: string) {
  const { data, error } = await supabase
    .from('judgments')
    .select('*')
    .eq('case_id', caseId)
    .order('effective_date', { ascending: false })

  if (error) throw error
  return data as Judgment[]
}

export async function createJudgment(judgmentData: Omit<Judgment, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('judgments')
    .insert(judgmentData)
    .select()
    .single()

  if (error) throw error
  return data as Judgment
}

// ============================================
// 仪表盘统计
// ============================================

export async function getDashboardStats(year?: number) {
  let query = supabase
    .from('cases')
    .select('status, case_type, party_role, claim_amount, filing_date')
    .is('deleted_at', null)

  if (year) {
    query = query.gte('filing_date', `${year}-01-01`).lte('filing_date', `${year}-12-31`)
  }

  const { data: cases, error } = await query
  if (error) throw error

  const stats = {
    closed: 0,
    active: 0,
    pending: 0,
    totalClaimAmount: 0,
    byType: {} as Record<string, number>,
    byRole: {} as Record<string, number>,
  }

  for (const c of cases) {
    switch (c.status) {
      case 'closed': stats.closed++ ; break
      case 'active': stats.active++ ; break
      case 'pending': stats.pending++ ; break
    }
    if (c.claim_amount) stats.totalClaimAmount += c.claim_amount
    stats.byType[c.case_type] = (stats.byType[c.case_type] || 0) + 1
    stats.byRole[c.party_role] = (stats.byRole[c.party_role] || 0) + 1
  }

  // 获取收款统计
  const { data: payments } = await supabase
    .from('payments')
    .select('amount, payment_date')
    .gte('payment_date', year ? `${year}-01-01` : '2000-01-01')
    .lte('payment_date', year ? `${year}-12-31` : '2099-12-31')

  const { data: judgments } = await supabase
    .from('judgments')
    .select('payable_amount, effective_date')
    .gte('effective_date', year ? `${year}-01-01` : '2000-01-01')
    .lte('effective_date', year ? `${year}-12-31` : '2099-12-31')

  const totalPayable = judgments?.reduce((sum: number, j: any) => sum + (j.payable_amount || 0), 0) || 0
  const totalReceived = payments?.reduce((sum: number, p: any) => sum + p.amount, 0) || 0

  return {
    ...stats,
    totalPayable,
    totalReceived,
    pendingAmount: totalPayable - totalReceived,
  }
}

export async function getUpcomingNodes(daysAhead = 7) {
  // Query nodes table directly instead of RPC
  const { data, error } = await supabase
    .from('nodes')
    .select(`
      id,
      case_id,
      node_name,
      end_date,
      priority,
      status,
      action_required,
      cases (
        id,
        case_number,
        title
      )
    `)
    .gte('end_date', new Date().toISOString().split('T')[0])
    .lte('end_date', new Date(Date.now() + daysAhead * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
    .order('end_date', { ascending: true })

  if (error) throw error

  // Transform to Notification-like format for Dashboard
  return (data || []).map((n: any) => ({
    id: n.id,
    user_id: '',
    node_id: n.id,
    case_id: n.case_id,
    type: 'deadline_reminder' as const,
    title: n.node_name,
    body: `${n.cases?.case_number || ''} — ${n.action_required || ''}`,
    read: false,
    created_at: new Date().toISOString(),
    priority: n.priority,
  }))
}

// ============================================
// 提醒配置
// ============================================

export async function getReminderConfig() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('reminder_config')
    .select('*')
    .eq('user_id', user.id)
    .single()

  if (error && error.code !== 'PGRST116') throw error
  return data as ReminderConfig | null
}

export async function upsertReminderConfig(config: Omit<ReminderConfig, 'id' | 'created_at' | 'updated_at'>) {
  const existing = await getReminderConfig()

  if (existing) {
    const { data, error } = await supabase
      .from('reminder_config')
      .update(config)
      .eq('id', existing.id)
      .select()
      .single()

    if (error) throw error
    return data as ReminderConfig
  } else {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { data, error } = await supabase
      .from('reminder_config')
      .insert({ ...config, user_id: user.id })
      .select()
      .single()

    if (error) throw error
    return data as ReminderConfig
  }
}

// ============================================
// 通知管理
// ============================================

export async function markNotificationAsRead(notificationId: string) {
  const { data, error } = await supabase
    .from('notifications')
    .update({ read: true, read_at: new Date().toISOString() })
    .eq('id', notificationId)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function markAllNotificationsAsRead() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { error } = await supabase
    .from('notifications')
    .update({ read: true, read_at: new Date().toISOString() })
    .eq('user_id', user.id)
    .eq('read', false)

  if (error) throw error
}

export async function getUnreadNotifications() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', user.id)
    .eq('read', false)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data as Notification[]
}
